import { useState } from 'react';
import { Button } from '../ui/button';
import { Wand2, Loader2, Check, Globe } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const LANGUAGE_NAMES = {
  ca: 'Català',
  es: 'Español', 
  fr: 'Français',
  en: 'English'
};

/**
 * TranslateButton - Reusable component for automatic translation
 * @param {string} text - The text to translate
 * @param {string} sourceLanguage - Source language code (ca, es, fr, en)
 * @param {function} onTranslated - Callback with translations { es: "...", fr: "...", en: "..." } and translatedLanguages array
 * @param {function} onLanguageSwitch - Optional callback to switch to a specific language tab
 * @param {boolean} disabled - Whether the button is disabled
 * @param {string} className - Additional CSS classes
 * @param {boolean} showFullLabel - Show full label with icon
 */
export const TranslateButton = ({ 
  text, 
  sourceLanguage = 'ca', 
  onTranslated, 
  onLanguageSwitch,
  disabled = false,
  className = '',
  showFullLabel = false
}) => {
  const [isTranslating, setIsTranslating] = useState(false);

  const handleTranslate = async () => {
    if (!text || !text.trim()) {
      toast.error('No hi ha text per traduir');
      return;
    }

    setIsTranslating(true);
    try {
      const targetLanguages = ['ca', 'es', 'fr', 'en'].filter(l => l !== sourceLanguage);
      
      const response = await fetch(`${API_URL}/api/translate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          text: text.trim(),
          source_language: sourceLanguage,
          target_languages: targetLanguages
        })
      });

      if (response.ok) {
        const data = await response.json();
        
        // Build list of translated languages for display
        const translatedLangs = Object.keys(data.translations)
          .map(code => LANGUAGE_NAMES[code] || code)
          .join(', ');
        
        // Show success toast with clear instructions
        toast.success(
          <div className="space-y-2">
            <div className="flex items-center gap-2 font-medium">
              <Check className="w-4 h-4 text-green-600" />
              Traducció completada!
            </div>
            <p className="text-sm text-gray-600">
              S'han generat traduccions per: <strong>{translatedLangs}</strong>
            </p>
            <p className="text-sm text-amber-600 font-medium">
              → Revisa cada idioma i clica "Guardar" per aplicar els canvis
            </p>
          </div>,
          { 
            duration: 6000,
            icon: <Globe className="w-5 h-5 text-[#1A4D2E]" />
          }
        );
        
        if (onTranslated) {
          // Pass translations and list of translated language codes
          onTranslated(data.translations, targetLanguages);
        }
        
        // Auto-switch to first translated language if callback provided
        if (onLanguageSwitch && targetLanguages.length > 0) {
          // Small delay to let state update first
          setTimeout(() => {
            onLanguageSwitch(targetLanguages[0]);
          }, 500);
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Error generant traducció');
      }
    } catch (error) {
      console.error('Translation error:', error);
      toast.error('Error de connexió');
    } finally {
      setIsTranslating(false);
    }
  };

  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      onClick={handleTranslate}
      disabled={disabled || isTranslating || !text || !text.trim()}
      className={`text-xs gap-1.5 text-[#1A4D2E] hover:text-[#1A4D2E] hover:bg-[#1A4D2E]/10 ${className}`}
      title="Traduir automàticament als altres idiomes (ES, FR, EN)"
    >
      {isTranslating ? (
        <Loader2 className="w-3.5 h-3.5 animate-spin" />
      ) : (
        <Wand2 className="w-3.5 h-3.5" />
      )}
      {isTranslating ? 'Traduint...' : (showFullLabel ? 'Traduir a altres idiomes' : 'Traduir')}
    </Button>
  );
};

export default TranslateButton;
